<template>
  <v-container v-if="service" class="main--text seo-service-page">
    <v-row>
      <v-col cols="12">
        <nav class="breadcrumbs">
          <router-link to="/">Home</router-link>
          <span>/</span>
          <a href="/#services">Services</a>
          <span>/</span>
          <span>{{ service.name }}</span>
        </nav>
      </v-col>
    </v-row>

    <v-row class="hero-row" align="center">
      <v-col md="7" cols="12">
        <p class="eyebrow">Dash Waste service</p>
        <h1>{{ service.h1 }}</h1>
        <p class="lede">{{ service.description }}</p>
        <div class="cta-row">
          <v-btn color="primary" height="52" class="mr-3 mb-3" @click="startQuote">
            Start a quote
          </v-btn>
          <a class="phone-link mb-3" :href="`tel:${site.phone}`" @click="trackPhone">
            {{ site.phoneDisplay }}
          </a>
        </div>
      </v-col>
      <v-col md="5" cols="12">
        <div class="fact-panel">
          <h2>Availability</h2>
          <p>{{ site.areaQualifier }}</p>
          <ul>
            <li v-for="area in site.serviceAreas" :key="area">{{ area }}</li>
          </ul>
        </div>
      </v-col>
    </v-row>

    <v-row class="content-row">
      <v-col md="4" cols="12">
        <v-card outlined class="cards">
          <v-card-title> 
            <h2>Best for</h2>
          </v-card-title>
          <v-card-text>
            <ul>
              <li v-for="item in service.bestFor" :key="item">{{ item }}</li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="4" cols="12">
        <v-card outlined class="cards">
          <v-card-title>
            <h2>Waste types</h2>
          </v-card-title>
          <v-card-text>
            <ul>
              <li v-for="item in service.wasteTypes" :key="item">{{ item }}</li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col md="4" cols="12">
        <v-card outlined class="cards">
          <v-card-title>
            <h2>Products</h2>
          </v-card-title>
          <v-card-text>
            <ul>
              <li v-for="item in service.containers" :key="item">{{ item }}</li>
            </ul>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <v-row class="content-row">
      <v-col md="6" cols="12">
        <h2>How to book</h2>
        <ol>
          <li v-for="step in bookingSteps" :key="step">{{ step }}</li>
        </ol>
      </v-col>
      <v-col md="6" cols="12">
        <h2>Notes before booking</h2>
        <ul>
          <li v-for="note in service.bookingNotes" :key="note">{{ note }}</li>
        </ul>
      </v-col>
    </v-row>

    <v-row class="content-row" v-if="service.faqs.length">
      <v-col cols="12">
        <h2>Common questions</h2>
        <div class="faq-list">
          <section v-for="item in service.faqs" :key="item.question">
            <h3>{{ item.question }}</h3>
            <p>{{ item.answer }}</p>
          </section>
        </div>
      </v-col>
    </v-row>

    <v-row class="content-row service-links">
      <v-col cols="12">
        <h2>Other waste services</h2>
        <router-link
          v-for="item in otherServices"
          :key="item.slug"
          :to="item.path"
          class="service-link"
        >
          {{ item.name }}
        </router-link>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import seoContent from '@/content/seo-content';
import { trackContactClick, trackEvent } from '@/utils/analytics';

export default {
  name: 'ServicePage',
  computed: {
    site() {
      return seoContent.site;
    },
    bookingSteps() {
      return seoContent.bookingSteps;
    },
    service() {
      return seoContent.getServiceBySlug(this.$route.params.slug);
    },
    otherServices() {
      if (!this.service) {
        return [];
      }
      return seoContent.services.filter((service) => service.slug !== this.service.slug);
    }
  },
  methods: {
    startQuote() {
      if (!this.service) {
        return;
      }
      trackEvent('quote_started', {
        quote_source: `service_page:${this.service.slug}`,
        service_type: this.service.serviceType
      });
      window.location.href = '/#location';
    },
    trackPhone() {
      trackContactClick('phone');
    }
  },
  mounted() {
    scrollTo(0, 0);
  }
}
</script>

<style scoped>
.seo-service-page {
  font-family: Avenir3;
  padding-top: 48px;
  padding-bottom: 80px;
}

.breadcrumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  margin-bottom: 24px;
}

.breadcrumbs a {
  color: #00AF66;
  text-decoration: none;
}

.hero-row {
  min-height: 420px;
}

.eyebrow {
  color: #00AF66;
  font-family: Avenir1;
  font-size: 18px;
  margin-bottom: 12px;
}

h1,
h2,
h3 {
  font-family: Avenir1;
}

h1 {
  font-size: 48px;
  line-height: 1.12;
  margin-bottom: 18px;
}

.lede {
  font-size: 22px;
  line-height: 1.55;
  max-width: 780px;
}

.cards {
  background: transparent;
  height: 300px;
}

.cta-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-top: 28px;
}

.phone-link {
  color: #00AF66;
  display: inline-flex;
  font-family: Avenir1;
  font-size: 22px;
  min-height: 52px;
  align-items: center;
  text-decoration: none;
}

.fact-panel {
  background: #1B2C37;
  border-radius: 8px;
  color: white;
  padding: 28px;
}

.fact-panel h2 {
  color: #00AF66;
}

.content-row {
  margin-top: 36px;
  margin-bottom: 36px;
}

.content-row li {
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 8px;
}

.faq-list section {
  border-top: 1px solid rgba(27, 44, 55, 0.18);
  padding: 20px 0;
  font-size: 18px;
}

.service-link {
  border: 1px solid rgba(27, 44, 55, 0.2);
  border-radius: 8px;
  color: #1B2C37;
  display: inline-flex;
  margin: 8px 8px 0 0;
  padding: 12px 16px;
  text-decoration: none;
}

.service-link:hover {
  border-color: #00AF66;
  color: #00AF66;
}

@media screen and (max-width: 780px) {
  h1 {
    font-size: 36px;
  }

  .lede {
    font-size: 19px;
  }

  .cards {
    height: 100%;
  }

  .content-row {
    margin-bottom: 100px;
  }
}
</style>
